import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs';

import { DashboardGralDto } from '../model/dto/DashboardGralDto';
import { RequestResponse } from '../model/dto/RequestResponse';
import { DashboardPriceChangeDto } from '../model/dto/DashboardPriceChangeDto';
import { TaskCountryOrganizationDto } from '../model/dto/TaskCountryOrganizationDto';
import { DashboardInventoryDetDto } from '../model/dto/DashboardInventoryDetDto';
import { isNullOrUndefined } from '@swimlane/ngx-datatable';
import { ComboWeekYearsDto } from '../model/dto/ComboWeekYearsDto';
import { DashboardWareEntryMonthly } from '../model/dto/DashboardWareEntryMonthly';
import { DashboardWareEntryAnnual } from '../model/dto/DashboardWareEntryAnnual';


@Injectable()
export class DashboardFacade {
    // #region Attributes

    private _urlBase: string;
    private _httpClient: HttpClient;

    // #endregion Attributes

    // #region Constructor

    constructor(http: HttpClient, @Inject('BASE_URL') baseUrl: string) {
        this._urlBase = baseUrl;
        this._httpClient = http;
    }

    // #endregio

    // #region  Public Methods

    GetGralReport(): Observable<RequestResponse<DashboardGralDto>> {
        return this._httpClient.get<RequestResponse<DashboardGralDto>>(this._urlBase + 'api/Dashboard/Gral');
    }

    GralGralReportCountryOrganization(taskCountryOrganizationDto: TaskCountryOrganizationDto):
        Observable<RequestResponse<DashboardGralDto>> {
        return this._httpClient.post<RequestResponse<DashboardGralDto>>(this._urlBase
            + 'api/Dashboard/GralCountryOrganization', taskCountryOrganizationDto);
    }

    GralGralReportOrganizations(taskCountryOrganizationDto: TaskCountryOrganizationDto):
        Observable<RequestResponse<DashboardGralDto>> {
        return this._httpClient.post<RequestResponse<DashboardGralDto>>(this._urlBase
            + 'api/Dashboard/GralOrganizations', taskCountryOrganizationDto);
    }

    GralGralReportByOrganizations(taskCountryOrganizationDto: TaskCountryOrganizationDto):
        Observable<RequestResponse<DashboardGralDto>> {
        return this._httpClient.post<RequestResponse<DashboardGralDto>>(this._urlBase
            + 'api/Dashboard/Gral/ByOrganizations', taskCountryOrganizationDto);
    }

    GetGralIndicator(type: number, goBack: boolean, lastDate: String, campaignCalendarId: number, week: number): Observable<any> {
        return this._httpClient.get<any>(
            this._urlBase + 'api/Dashboard/GralIndicator?catDetTypeId=' + type
            + '&goBack=' + goBack + '&lastDate=' + lastDate + '&campaignCalendarId=' + campaignCalendarId
            + '&week=' + week);
    }

    GetGralIndicatorCountryOrganization(taskCountryOrganizationDto: TaskCountryOrganizationDto):
        Observable<RequestResponse<DashboardGralDto>> {
        return this._httpClient.post<RequestResponse<DashboardGralDto>>(this._urlBase
            + 'api/Dashboard/GralIndicatorCountryOrganization', taskCountryOrganizationDto);
    }

    GetGralIndicatorByOrganization(taskCountryOrganizationDto: TaskCountryOrganizationDto):
        Observable<RequestResponse<DashboardGralDto>> {
        return this._httpClient.post<RequestResponse<DashboardGralDto>>(this._urlBase
            + 'api/Dashboard/GralIndicatorByOrganization', taskCountryOrganizationDto);
    }

    GetGralIndicatorChecklist(type: number, goBack: boolean, lastDate: String, organizationId: String): Observable<any> {
        return this._httpClient.get<any>(
            this._urlBase + 'api/Dashboard/GralIndicatorChecklist?catDetTypeId=' + type
            + '&goBack=' + goBack + '&lastDate=' + lastDate + '&organizationId=' + organizationId);
    }

    GetGralIndicatorChecklistByOrganization(type: number, goBack: boolean, lastDate: String, organizations: String): Observable<any> {
        return this._httpClient.get<any>(
            this._urlBase + 'api/Dashboard/GralIndicatorChecklistByOrganization?catDetTypeId=' + type
            + '&goBack=' + goBack + '&lastDate=' + lastDate + '&organizations=' + organizations);
    }

    GetChecklistDetail(type: number): Observable<any> {
        return this._httpClient.get<any>(this._urlBase + 'api/Dashboard/ChecklistDetail?type=' + type);
    }

    GetChecklistDetailCountryOrganization(taskCountryOrganizationDto: TaskCountryOrganizationDto):
        Observable<any> {
        return this._httpClient.post<any>(this._urlBase
            + 'api/Dashboard/ChecklistDetailCountryOrganization', taskCountryOrganizationDto);
    }

    GetChecklistOnDemandDetail(type: number, organizationId: string): Observable<any> {
        return this._httpClient.get<any>(this._urlBase
            + 'api/Dashboard/ChecklistOnDemandDetail?type=' + type + '&organizationId=' + organizationId);
    }

    GetChecklistOnDemandDetailByOrganization(type: number, organizations: string): Observable<any> {
        return this._httpClient.get<any>(this._urlBase
            + 'api/Dashboard/ChecklistOnDemandDetailByOrganization?type=' + type + '&organizations=' + organizations);
    }

    GetDashboardLevelChangeDet(campaignCalendarId: number, catDetTaskTypeId = 102, organizationId = ''): Observable<any> {
        return this._httpClient.get<any>(this._urlBase + 'api/Dashboard/DashboardLevelChangeDet?campaignCalendarId=' + campaignCalendarId + '&&catDetTaskTypeId=' + catDetTaskTypeId + '&&organizationId=' + organizationId);
    }

    GetDashboardLevelChangeDetByOrganization(campaignCalendarId: number, catDetTaskTypeId = 102, organizations = ''): Observable<any> {
        return this._httpClient.get<any>(this._urlBase + 'api/Dashboard/DashboardLevelChangeDet/ByOrganization?campaignCalendarId=' + campaignCalendarId + '&&organizations=' + organizations + '&&catDetTaskTypeId=' + catDetTaskTypeId);
    }

    GetMerchTrasCorpDetail(week: number, year: String, organizationId: String): Observable<any> {
        return this._httpClient.get<any>(this._urlBase
            + 'api/Dashboard/MerchTrasCorp?week=' + week + '&year=' + year + '&organizationId=' + organizationId);
    }

    GetReturnCorpDetail(month: number, year: number, organizationId: String): Observable<any> {
        return this._httpClient.get<any>(this._urlBase
            + 'api/Dashboard/ReturnCorp?month=' + month + '&year=' + year + '&organizationId=' + organizationId);
    }

    GetWareEntryDetail(week: number, year: string, organizationId: string): Observable<any> {
        let params = new HttpParams();

        if (organizationId == "" || isNullOrUndefined(organizationId)) {
            params = params.append("week", week.toString());
            params = params.append("year", year);
        }
        else {
            params = params.append("week", week.toString());
            params = params.append("year", year);
            params = params.append("organizationId", organizationId);
        }

        return this._httpClient.get<any>(this._urlBase + 'api/Dashboard/WareEntry', { params: params })
    }

    GetMerchTrasCorpDetailByOrganization(week: number, year: String, organizations: String): Observable<any> {
        return this._httpClient.get<any>(this._urlBase
            + 'api/Dashboard/MerchTrasCorp/ByOrganization?week=' + week + '&year=' + year + '&organizations=' + organizations);
    }

    GetMerchReturnDetail(organizationId: String, catDetCountryId: Number): Observable<any> {
        return this._httpClient.get<any>(this._urlBase
            + 'api/Dashboard/MerchReturn?organizationId=' + organizationId + '&catDetCountryId=' + catDetCountryId);
    }

    GetMerchTrasStoreDetail(region: number, corpIsChecked: boolean, storeIsChecked: boolean): Observable<any> {
        return this._httpClient.get<any>(
            this._urlBase + 'api/Dashboard/MerchTrasStoreDet?region=' + region
            + '&corpIsChecked=' + corpIsChecked + '&storeIsChecked=' + storeIsChecked);
    }

    GetMerchInventoryDetail(organizationId = ''): Observable<any> {
        return this._httpClient.get<any>(this._urlBase + 'api/Dashboard/MerchInventory?organizationId=' + organizationId);
    }

    GetMerchInventoryDetailByOrganizations(organizations: string): Observable<RequestResponse<DashboardInventoryDetDto[]>> {
        return this._httpClient.get<RequestResponse<DashboardInventoryDetDto[]>>(this._urlBase + 'api/Dashboard/MerchInventory/ByOrganizations?organizations=' + organizations);
    }
    /*
    GetMerchReturnStoreDetail(region:number, corpIsChecked: boolean, storeIsChecked:boolean): Observable<any> {
        return this._httpClient.get<any>(
            this._urlBase + 'api/Dashboard/MerchReturnStoreDet?region=' + region
            + '&corpIsChecked='+ corpIsChecked + '&storeIsChecked='+ storeIsChecked);
    }
    */

    GetChecklistHistory(catDetCountryId: Number = 123, organizations: string = ''): Observable<any> {
        return this._httpClient.get<any>(this._urlBase + 'api/Dashboard/History?catDetCountryId='
            + catDetCountryId + '&&organizations=' + organizations);
    }

    GetChecklistHistoryByOrganization(organizations: string): Observable<any> {
        return this._httpClient.get<any>(this._urlBase + 'api/Dashboard/History/ByOrganization?organizations=' + organizations);
    }

    GetMerchTrasStoreHistory(corpIsChecked: boolean, storeIsChecked: boolean, organizations = ''): Observable<any> {
        return this._httpClient.get<any>(
            this._urlBase + 'api/Dashboard/HistoryMerchTras?corpIsChecked=' + corpIsChecked
            + '&storeIsChecked=' + storeIsChecked + '&organizationId=' + organizations);
    }

    GetMerchTrasStoreHistoryByOrganization(corpIsChecked: boolean, storeIsChecked: boolean, organizations: string): Observable<any> {
        return this._httpClient.get<any>(
            this._urlBase + 'api/Dashboard/HistoryMerchTras/ByOrganization?corpIsChecked=' + corpIsChecked
            + '&storeIsChecked=' + storeIsChecked + '&organizations=' + organizations);
    }

    GetDashboardPriceChangesDet(campaignCalendarId: number, organizationId: string = ''): Observable<RequestResponse<any>> {
        return this._httpClient.get<RequestResponse<any>>(
            this._urlBase + 'api/Dashboard/DashboardPriceChangeDet?campaignCalendarId='
            + campaignCalendarId + '&organizationId=' + organizationId);
    }

    GetDashboardPriceChangesDetByOrganization(campaignCalendarId: number, organization: string): Observable<RequestResponse<any>> {
        return this._httpClient.get<RequestResponse<any>>(
            this._urlBase + 'api/Dashboard/DashboardPriceChangeDet/ByOrganization?campaignCalendarId='
            + campaignCalendarId + '&organizations=' + organization);
    }

    GetDashboardPriceChangesByCampaign(pcc: Number, organizationId: string = ''): Observable<RequestResponse<DashboardPriceChangeDto>> {
        return this._httpClient.get<RequestResponse<DashboardPriceChangeDto>>(this._urlBase + 'api/Dashboard/GralPricesByCampaign?campaignCalendarId=' + pcc + '&organizationId=' + organizationId);
    }

    GetDashboardPriceChangesByCampaignByOrganization(pcc: Number, organizations: string): Observable<RequestResponse<DashboardPriceChangeDto>> {
        return this._httpClient.get<RequestResponse<DashboardPriceChangeDto>>(this._urlBase + 'api/Dashboard/GralPricesByCampaign/ByOrganization?campaignCalendarId=' + pcc + '&organizations=' + organizations);
    }

    GetHistoricWeeks(): Observable<any> {
        return this._httpClient.get<RequestResponse<ComboWeekYearsDto>>(this._urlBase + 'api/Dashboard/GetWeeks');        
    }

    GetDashboardWareEntryMonthly(organizations: string): Observable<RequestResponse<DashboardWareEntryMonthly[]>> {
        return this._httpClient.get<RequestResponse<DashboardWareEntryMonthly[]>>(this._urlBase + 'api/Dashboard/WareEntryMonthly?organizations=' + organizations);
    }

    GetDashboardWareEntryAnnual(organizations: string): Observable<RequestResponse<DashboardWareEntryAnnual[]>> {
        return this._httpClient.get<RequestResponse<DashboardWareEntryAnnual[]>>(this._urlBase + 'api/Dashboard/WareEntryAnnual?organizations=' + organizations);
    }
    // #endregion Public Methods
}
